import { Button, LoadingOverlay } from "@sunrun/experience-ui-components";
import styled from "@emotion/styled/macro";
import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { isAxiosError } from "axios";
import { setJwtCache } from "services/jwtCache";
import { BASE_URLS } from "config";
import { getEnv } from "env";
import { getHybridToken } from "services/hybridAuthService";
import { OktaToken } from "services/oktaService";
import {
  SfdcToken,
  exchangeAuthCode,
  retrieveAndClearPkceVerifier,
} from "services/splatAuthService";
import { setRefreshTokenCache } from "services/refreshTokenCache";
import { postMessage } from "messages";
import sunrunLogo from "assets/sunrun-logo.svg";
import {
  AUTH__CODE_EXCHANGE_INITIATED,
  AUTH__TOKEN_TRANSFER_INITIATED,
} from "constants/mixpanelEvents";
import { Mixpanel } from "providers/mixpanel";

const SplatLoginPage = () => {
  const [errorMessage, setErrorMessage] = useState<string>();
  const [viewDetails, setViewDetails] = useState(false);
  const [loading, setLoading] = useState(true);
  const env = getEnv();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    const handleError = (error: any) => {
      if (isAxiosError(error)) {
        setErrorMessage(JSON.stringify(error.response?.data));
      } else if (error.message) {
        setErrorMessage(error.message);
      } else {
        setErrorMessage(JSON.stringify(error));
      }
      setLoading(false);
    };

    const completeLogin = (
      hybridToken: string,
      tokenType: "okta" | "salesforce"
    ) => {
      setJwtCache({
        token: hybridToken as any,
        tokenType,
        source: "splat",
      });
      postMessage({
        type: "EVENT:AUTH_TOKEN",
        source: "Auth",
        payload: { tokenType, token: hybridToken },
      });
      postMessage({
        type: "EVENT:LOGIN_SUCCESS",
        source: "Auth",
      });
      setLoading(false);
    };

    const code = searchParams.get("code");
    const oktaToken = searchParams.get("oktaToken") as OktaToken;
    const sfdcToken = searchParams.get("sfdcToken") as SfdcToken;

    if (code) {
      Mixpanel.track(AUTH__CODE_EXCHANGE_INITIATED, {});
      // Direct PKCE flow: exchange authorization code for Salesforce token
      const codeVerifier = retrieveAndClearPkceVerifier();
      if (!codeVerifier) {
        setErrorMessage(
          "Missing PKCE code verifier. Please try logging in again."
        );
        setLoading(false);
        return;
      }
      exchangeAuthCode(code, codeVerifier)
        .then(({ sfdcToken: accessToken, refreshToken }) => {
          if (refreshToken) {
            setRefreshTokenCache(refreshToken);
          }
          return getHybridToken("salesforce", accessToken);
        })
        .then(({ hybridToken }) => completeLogin(hybridToken, "salesforce"))
        .catch(handleError);
    } else if (oktaToken || sfdcToken) {
      Mixpanel.track(AUTH__TOKEN_TRANSFER_INITIATED, {
        tokenType: oktaToken ? "okta" : "salesforce",
      });
      // Legacy flow: token passed directly via query params
      getHybridToken(oktaToken ? "okta" : "salesforce", oktaToken || sfdcToken)
        .then(({ hybridToken }) =>
          completeLogin(hybridToken, oktaToken ? "okta" : "salesforce")
        )
        .catch(handleError);
    } else {
      setErrorMessage("No token provided.");
      setLoading(false);
    }
    // Intentionally empty dependency array to run only once
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const navigateToLogin = useCallback(() => {
    postMessage({
      type: "COMMAND:CHANGE_URL",
      source: "Auth",
      payload: {
        url: `${BASE_URLS.CLIENT[env]}/login`,
      },
    });
  }, [env]);

  return (
    <Page>
      {loading && <LoadingOverlay message="Sending you to ONE..." />}
      {errorMessage && (
        <PageCenter>
          <img src={sunrunLogo} alt="Sunrun Logo" height={100} />
          <div style={{ marginTop: 16 }}>
            There was an error logging you in. Please{" "}
            <InlineButton onClick={navigateToLogin}>try again</InlineButton>.
          </div>
          <div>If this problem persists, please contact support.</div>
          <Button onClick={() => setViewDetails(!viewDetails)}>
            {viewDetails ? "Hide Details" : "View Details"}
          </Button>
          {viewDetails && (
            <div>
              <pre>{errorMessage}</pre>
            </div>
          )}
        </PageCenter>
      )}
    </Page>
  );
};

const Page = styled.div`
  height: 100vh;
  width: 100vw;
  background-color: #fffbf0;
`;

const PageCenter = styled.div`
  padding-top: 20vh;

  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
`;

const InlineButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  position: relative;
  color: blue;
`;

export { SplatLoginPage };
